import AiStarIcon from "@redotech/redo-icons/ai-star.svg";
import { FlowType, ReturnFlow } from "@redotech/redo-model/return-flow";
import { AiSlideOutChat } from "@redotech/redo-web/ai/chat/components/slide-out-chat-modal";
import { AiChatProvider } from "@redotech/redo-web/ai/chat/contexts/ai-chat-context";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { memo } from "react";
import * as styles from "./flow-agent-chat.module.css";
import { useFlowAgentChat } from "./use-flow-agent-chat";

export const FlowAgentChat = memo(function FlowAgentChat({
  flow,
  flowType,
  onFlowChange,
}: {
  flow?: ReturnFlow;
  flowType: FlowType;
  onFlowChange: (flow: ReturnFlow) => void;
}) {
  const {
    showChat,
    setShowChat,
    showChatButton,
    handleSendMessageToAI,
    conversationId,
    loading,
  } = useFlowAgentChat({ flow, flowType, onFlowChange });

  if (!showChatButton) {
    return null;
  }

  // Dynamic welcome message based on whether there's an existing flow
  const hasExistingFlow = flow?.steps && flow.steps.length > 0;
  const welcomeMessage = hasExistingFlow
    ? "👋 I can help you understand this flow or create a new one!\n\n" +
      "**Ask questions like:**\n" +
      "• What does this flow do?\n" +
      "• What happens if someone tries to return after 30 days?\n" +
      "• Who pays for shipping?\n\n" +
      "**Or request changes:**\n" +
      "• Create a new 60-day return policy\n" +
      "• Make a flow that allows final sale returns\n\n" +
      "What would you like to know or do?"
    : "👋 I can create a custom return flow for you!\n\n" +
      "Just describe your return policy requirements and I'll build a complete flow.\n\n" +
      "**For example:**\n" +
      "• Create a 30-day return policy with exchanges\n" +
      "• Build a flow that requires photos for damage claims\n" +
      "• Make a policy with different rules for final sale items\n\n" +
      "What kind of return flow do you need?";

  const buttonText = hasExistingFlow
    ? "Ask AI or Create Flow"
    : "Generate with AI";

  const chatTitle = "AI Flow Assistant";

  return (
    <>
      <div className={styles.aiButtonContainer}>
        <RedoButton
          buttonClassName={styles.orangeButton}
          disabled={loading}
          hierarchy="primary"
          IconLeading={AiStarIcon}
          onClick={() => setShowChat(true)}
          size="sm"
          text={buttonText}
          theme="normal"
        />
      </div>
      <AiChatProvider
        conversationId={conversationId}
        defaultMessages={[
          { id: "welcome", role: "assistant", content: welcomeMessage },
        ]}
        sendMessageToAI={handleSendMessageToAI}
      >
        <AiSlideOutChat
          isBeta
          onClose={() => setShowChat(false)}
          open={showChat}
          title={chatTitle}
        />
      </AiChatProvider>
    </>
  );
});
